import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trophy, Medal, Crown, TrendingUp, Loader2, ChevronRight } from 'lucide-react';
import { apiRequest, cn } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';

export default function LeaderboardWidget() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchLeaderboard();
  }, []);

  const fetchLeaderboard = async () => {
    try {
      const res = await apiRequest('/recognition/points-leaderboard?period=month&limit=5');
      setData(res);
    } catch (err) {
      setError('Failed to load leaderboard');
    } finally {
      setLoading(false);
    }
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Crown className="text-yellow-500" size={18} />;
    if (rank === 2) return <Medal className="text-gray-400" size={16} />;
    if (rank === 3) return <Medal className="text-amber-600" size={16} />;
    return <span className="text-sm font-medium text-slate-400">#{rank}</span>;
  };

  if (loading) {
    return (
      <Card className="h-full">
        <CardContent className="flex items-center justify-center h-48">
          <Loader2 className="animate-spin text-slate-400" size={24} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full" data-testid="leaderboard-widget">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center justify-between text-base">
          <span className="flex items-center gap-2">
            <Trophy className="text-yellow-500" size={18} />
            Leaderboard
          </span>
          <Button variant="ghost" size="sm" onClick={() => navigate('/gamification')}>
            <ChevronRight size={16} />
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {data?.my_rank && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-2 mb-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-red-700">Your Rank</span>
              <span className="font-bold text-red-700">#{data.my_rank}</span>
            </div>
            <div className="flex items-center justify-between text-xs text-red-600">
              <span>Points</span>
              <span>{data.my_points || 0}</span>
            </div>
          </div>
        )}
        
        {data?.leaderboard?.length > 0 ? (
          <div className="space-y-2">
            {data.leaderboard.slice(0, 5).map((entry, idx) => (
              <div 
                key={entry.user_id}
                className={cn(
                  "flex items-center gap-2 p-2 rounded-lg",
                  idx === 0 ? "bg-yellow-50" : "bg-slate-50"
                )}
              >
                <div className="w-6 flex justify-center">
                  {getRankIcon(entry.rank)}
                </div>
                <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-sm font-medium">
                  {entry.name?.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{entry.name}</p>
                </div>
                <div className="text-sm font-bold text-slate-700">
                  {entry.total_points}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-4 text-slate-500 text-sm">
            <Trophy size={24} className="mx-auto mb-2 opacity-30" />
            No points earned yet
          </div>
        )}
      </CardContent>
    </Card>
  );
}
